import { Link } from 'react-router-dom';
import SEO from '@/components/base/SEO';
import BlogArticleLayout from '@/components/base/BlogArticleLayout';

const breadcrumb = [
  { label: 'Accueil', to: '/' },
  { label: 'Blog', to: '/blog/' },
  { label: 'Double vitrage embué' },
];

const jsonLdBlogPosting = {
  '@context': 'https://schema.org',
  '@type': 'BlogPosting',
  headline: 'Double vitrage embué dans l\'Hérault : que faire',
  description: 'Buée persistante entre les deux vitres d\'un double vitrage ? Joint périphérique HS, perte d\'isolation, solutions : remplacer le vitrage sans changer la fenêtre. Hérault.',
  url: 'https://heraultroulettesbaiesvitrees.fr/blog/double-vitrage-embue/',
  datePublished: '2026-03-18',
  dateModified: '2026-07-26',
  author: { '@type': 'Organization', name: 'DFPRO Hérault' },
  publisher: { '@id': 'https://heraultroulettesbaiesvitrees.fr/#business' },
};

const jsonLdFAQ = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: [
    {
      '@type': 'Question',
      name: 'Pourquoi de la buée apparaît-elle entre les deux vitres d\'un double vitrage ?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'La buée entre les vitres indique que le joint périphérique du double vitrage est défaillant. Ce joint assure l\'étanchéité de la lame d\'air ou de gaz (argon) entre les deux vitres. Quand il se dégrade, l\'humidité de l\'air ambiant s\'infiltre et se condense sur la face intérieure des vitres. Le double vitrage a perdu ses propriétés isolantes.',
      },
    },
    {
      '@type': 'Question',
      name: 'Faut-il changer toute la fenêtre quand le double vitrage est embué ?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Non, dans la très grande majorité des cas. Le vitrage est une pièce indépendante du cadre (PVC, alu ou bois). On peut remplacer uniquement le vitrage isolant — ce qu\'on appelle un remplacement de vitrage en rénovation — sans toucher au cadre de la fenêtre ou de la baie coulissante.',
      },
    },
    {
      '@type': 'Question',
      name: 'Un double vitrage embué fait-il perdre beaucoup d\'isolation ?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Oui, significativement. La présence d\'humidité dans la lame d\'air ou de gaz réduit considérablement les performances thermiques et phoniques du vitrage. Un double vitrage embué est quasiment aussi peu isolant qu\'un simple vitrage. Le remplacer permet de retrouver les performances d\'origine et de réduire votre facture de chauffage.',
      },
    },
  ],
};

const jsonLdBreadcrumb = {
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: [
    { '@type': 'ListItem', position: 1, item: { '@id': 'https://heraultroulettesbaiesvitrees.fr/', name: 'Accueil' } },
    { '@type': 'ListItem', position: 2, item: { '@id': 'https://heraultroulettesbaiesvitrees.fr/blog/', name: 'Blog' } },
    { '@type': 'ListItem', position: 3, item: { '@id': 'https://heraultroulettesbaiesvitrees.fr/blog/double-vitrage-embue/', name: 'Double vitrage embué' } },
  ],
};

export default function ArticleDoubleVitrageEmbue() {
  return (
    <>
      <SEO
        heroImage="/images/blog-double-vitrage.jpg"
        title="Double vitrage embué : pourquoi et comment y remédier"
        description="Buée persistante dans votre double vitrage dans l'Hérault ? Joint HS, perte d'isolation. Remplacez le vitrage sans changer la fenêtre. Devis gratuit. DFPRO."
        canonicalUrl="https://heraultroulettesbaiesvitrees.fr/blog/double-vitrage-embue/"
      />
      <BlogArticleLayout
        title="Double vitrage embué dans l&apos;Hérault : que faire"
        breadcrumb={breadcrumb}
        jsonLdBlogPosting={jsonLdBlogPosting}
        jsonLdFAQ={jsonLdFAQ}
        jsonLdBreadcrumb={jsonLdBreadcrumb}
        datePublished="18 mars 2026"
        dateModified="26 juillet 2026"
      >
        <p>
          Vous avez beau frotter, la buée ne part pas. C&apos;est qu&apos;elle n&apos;est pas sur la vitre, mais <strong>entre</strong> les deux vitres de votre double vitrage. Ce phénomène, que l&apos;on appelle « double vitrage embué » ou « vitrage en opale », est le signe incontestable que votre vitrage isolant est en fin de vie. Dans l&apos;Hérault, où les écarts de température entre le jour et la nuit sont marqués, et où l&apos;humidité du littoral est omniprésente, ce problème touche de nombreux logements. Bonne nouvelle : il se résout sans changer toute la fenêtre.
        </p>

        <h2>Comment fonctionne un double vitrage ?</h2>
        <p>
          Un double vitrage est composé de deux feuilles de verre séparées par un espace hermétique rempli d&apos;air déshydraté ou, pour les modèles plus performants, de gaz argon. Sur le pourtour, un joint périphérique — appelé aussi « intercalaire » — assure l&apos;étanchéité de cette lame d&apos;air ou de gaz. C&apos;est cette couche isolante qui confère au double vitrage ses performances thermiques et phoniques. Quand le joint périphérique se dégrade, deux choses se produisent : l&apos;étanchéité est rompue, l&apos;humidité s&apos;infiltre, et une fois à l&apos;intérieur, elle se condense sur les faces internes des vitres. Cette buée permanente — qui ne part pas quand le soleil tape, contrairement à la condensation de surface — est la preuve que le vitrage n&apos;est plus étanche.
        </p>

        <h2>Pourquoi les joints de double vitrage lâchent-ils ?</h2>
        <p>
          Le vieillissement des joints périphériques est un phénomène naturel, accéléré par plusieurs facteurs. L&apos;<strong>exposition solaire</strong> est le premier ennemi : les rayons UV dégradent le mastic et les matériaux synthétiques du joint. Dans l&apos;Hérault, avec plus de 300 jours de soleil par an, les vitrages exposés plein sud sont particulièrement vulnérables. Les <strong>variations thermiques</strong> sont le deuxième facteur : la dilatation et la contraction quotidiennes du verre et de l&apos;intercalaire finissent par créer des microfissures dans le joint. Enfin, dans les zones littorales comme Sète, Agde ou Frontignan, l&apos;<strong>air salin</strong> attaque chimiquement les composants du joint, accélérant sa dégradation.
        </p>

        {/* CTA milieu */}
        <div className="my-8 p-5 md:p-6 bg-background-100 rounded-lg border border-accent-500/20 text-center">
          <p className="font-heading font-bold text-primary-500 text-base mb-2">
            Double vitrage embué dans l&apos;Hérault ?
          </p>
          <p className="text-sm text-foreground-500 font-body mb-4">
            <Link to="/double-vitrage-embue-herault/" className="text-accent-500 font-semibold hover:underline">Remplacement de vitrage embué</Link> sans changer la fenêtre. Devis gratuit, garantie 10 ans.
          </p>
          <a href="tel:+33980802242" className="btn-call text-sm px-6 py-3 whitespace-nowrap inline-flex">
            <span className="w-4 h-4 flex items-center justify-center">
              <i className="ri-phone-line text-sm"></i>
            </span>
            09 80 80 22 42
          </a>
        </div>

        <h2>Conséquences d&apos;un double vitrage embué</h2>
        <p>
          Au-delà de l&apos;aspect esthétique — une fenêtre ou une baie vitrée constamment trouble n&apos;est jamais agréable — les conséquences sont concrètes. La <strong>perte d&apos;isolation thermique</strong> est la plus problématique : un double vitrage embué perd une grande partie de sa résistance thermique et se comporte quasiment comme un simple vitrage. Résultat : votre logement est plus difficile à chauffer en hiver et se réchauffe plus vite en été, avec un impact direct sur votre confort et votre facture énergétique. La <strong>perte d&apos;isolation phonique</strong> est un autre effet : la lame d&apos;air ou de gaz joue un rôle d&apos;amortisseur acoustique, et sa dégradation rend votre logement plus sensible aux bruits extérieurs. Enfin, la présence persistante d&apos;humidité entre les vitres peut, à très long terme, entraîner l&apos;apparition de moisissures ou de traces minérales indélébiles sur la face interne des verres.
        </p>

        <h2>La solution : remplacer le vitrage, pas la fenêtre</h2>
        <p>
          C&apos;est la bonne nouvelle que nous annonçons à tous nos clients de Montpellier, Béziers ou Sète qui nous appellent pour ce problème : <strong>il n&apos;est pas nécessaire de changer la fenêtre ou la baie coulissante</strong>. Le vitrage est une pièce indépendante du cadre, qu&apos;il soit en PVC, en aluminium ou en bois. Le <Link to="/remplacement-double-vitrage-herault/" className="text-accent-500 font-semibold hover:underline">remplacement du double vitrage</Link> consiste à déposer les parcloses — les baguettes qui maintiennent le vitrage dans le cadre —, extraire l&apos;ancien vitrage, et poser un vitrage neuf aux dimensions exactes.
        </p>
        <p>
          L&apos;opération est technique mais maîtrisée : nos techniciens prennent les mesures au millimètre près, commandent le vitrage sur mesure aux dimensions de votre menuiserie, et procèdent au remplacement en une seule intervention. Le nouveau vitrage bénéficie des mêmes garanties que le vitrage d&apos;origine, et les performances thermiques et phoniques sont intégralement restaurées. Cerise sur le gâteau : vous pouvez en profiter pour passer d&apos;un double vitrage standard à un vitrage à isolation renforcée (VIR), avec un coefficient thermique nettement amélioré, pour un confort supérieur été comme hiver.
        </p>

        <h2>Ne pas confondre buée dans le vitrage et condensation de surface</h2>
        <p>
          Il est important de distinguer deux phénomènes qui se ressemblent mais n&apos;ont rien à voir. La <strong>condensation de surface</strong> — de la buée qui apparaît sur la face intérieure de la vitre, côté pièce, le matin ou après une douche — est un phénomène normal lié à l&apos;humidité de la pièce et à la différence de température entre l&apos;intérieur et l&apos;extérieur. Elle se résorbe en aérant ou en chauffant la pièce. La <strong>buée entre les vitres</strong>, au contraire, est piégée dans l&apos;espace hermétique et ne disparaît jamais. Si vous passez le doigt sur la vitre et que la buée ne s&apos;efface pas (parce qu&apos;elle est de l&apos;autre côté du verre), c&apos;est le signe que votre double vitrage est à remplacer.
        </p>

        <h2>Questions fréquentes</h2>

        <h3>Peut-on réparer un double vitrage embué sans le remplacer ?</h3>
        <p>
          Il existe des techniques de « désembuage » qui consistent à percer le vitrage, injecter un produit asséchant, puis reboucher le trou. Ces méthodes sont controversées : elles ne restaurent pas l&apos;étanchéité d&apos;origine, ne garantissent pas la durée du résultat, et peuvent fragiliser le vitrage. Nous recommandons le remplacement complet du vitrage, seule solution durable et garantie.
        </p>

        <h3>Le changement de vitrage est-il couvert par l&apos;assurance habitation ?</h3>
        <p>
          L&apos;usure naturelle du joint périphérique n&apos;est généralement pas couverte par les assurances habitation. En revanche, si le vitrage a été endommagé par un événement couvert — bris de vitre suite à une tempête, un acte de vandalisme, ou un choc accidentel — votre assurance peut prendre en charge le remplacement. Vérifiez votre contrat ou interrogez votre assureur.
        </p>
      </BlogArticleLayout>
    </>
  );
}